import { useParams, Navigate, Link } from 'react-router-dom';
import { Lightbulb, ArrowRight } from 'lucide-react';
import PageLayout from '../components/PageLayout';
import ProtectedRoute from '../components/ProtectedRoute';
import { HelpBreadcrumb, HelpStepList, HelpQuickActions } from '../components/help';
import CalloutCard from '../components/CalloutCard';
import { useHelpArticle, useRelatedArticles } from '../hooks/useHelpArticles';
import { HELP_CATEGORIES } from '../data/helpArticles';

interface HelpArticlePageProps {
  slug?: string; // For public routes with hardcoded slug
}

export default function HelpArticlePage({ slug: propSlug }: HelpArticlePageProps) {
  const params = useParams<{ category: string; slug: string }>();
  const slug = propSlug || params.slug || '';

  const { article } = useHelpArticle(slug);
  const relatedArticles = useRelatedArticles(article);

  // Redirect if article doesn't exist
  if (!article) {
    return <Navigate to="/help" replace />;
  }

  // Get category metadata for breadcrumb
  const categoryMeta = HELP_CATEGORIES.find((c) => c.slug === article.category);

  const content = (
    <PageLayout maxWidth="narrow" title={article.title}>
      <HelpBreadcrumb
        items={[
          { label: 'Help Center', href: '/help' },
          { label: categoryMeta?.title || article.category, href: `/help/${article.category}` },
          { label: article.title },
        ]}
      />

      {/* Article header */}
      <div className="mb-8">
        <h1
          className="text-2xl font-bold md:text-3xl"
          style={{ color: 'var(--text-primary)' }}
        >
          {article.title}
        </h1>
        <p
          className="mt-2 text-lg"
          style={{ color: 'var(--text-secondary)' }}
        >
          {article.description}
        </p>
        <p
          className="mt-2 text-sm"
          style={{ color: 'var(--text-muted)' }}
        >
          {article.duration} • {article.steps.length} steps
        </p>
      </div>

      {/* Steps */}
      <HelpStepList steps={article.steps} />

      {/* Tips section */}
      {article.tips && article.tips.length > 0 && (
        <div className="mt-8">
          <CalloutCard title="Pro Tips" variant="info">
            <ul className="space-y-2">
              {article.tips.map((tip, index) => (
                <li key={index} className="flex items-start gap-2">
                  <Lightbulb
                    className="h-4 w-4 mt-0.5 shrink-0"
                    style={{ color: 'var(--oro-gold)' }}
                  />
                  <span>{tip}</span>
                </li>
              ))}
            </ul>
          </CalloutCard>
        </div>
      )}

      {/* Quick action deep links (#1359) */}
      {article.quickActions && article.quickActions.length > 0 && (
        <HelpQuickActions actions={article.quickActions} />
      )}

      {/* Related articles */}
      {relatedArticles.length > 0 && (
        <div className="mt-10 pt-8 border-t" style={{ borderColor: 'var(--oro-border)' }}>
          <h2
            className="text-lg font-semibold mb-4"
            style={{ color: 'var(--text-primary)' }}
          >
            {article.nextArticle ? 'Next Up' : 'Related Articles'}
          </h2>
          <div className="space-y-3">
            {relatedArticles.map((related) => (
              <Link
                key={related.slug}
                to={`/help/${related.category}/${related.slug}`}
                className="flex items-center justify-between gap-4 rounded-lg border p-4 transition-all hover:border-[var(--oro-gold)]"
                style={{
                  backgroundColor: 'var(--oro-surface)',
                  borderColor: 'var(--oro-border)',
                }}
              >
                <div>
                  <h3
                    className="font-medium"
                    style={{ color: 'var(--text-primary)' }}
                  >
                    {related.title}
                  </h3>
                  <p
                    className="text-sm mt-0.5"
                    style={{ color: 'var(--text-secondary)' }}
                  >
                    {related.description}
                  </p>
                </div>
                <ArrowRight
                  className="h-5 w-5 shrink-0"
                  style={{ color: 'var(--oro-gold)' }}
                />
              </Link>
            ))}
          </div>
        </div>
      )}

      {/* Back to category */}
      <div className="mt-10 pt-6 border-t" style={{ borderColor: 'var(--oro-border)' }}>
        <Link
          to={`/help/${article.category}`}
          className="text-sm font-medium hover:underline"
          style={{ color: 'var(--oro-gold)' }}
        >
          ← Back to {categoryMeta?.title || 'Help'}
        </Link>
      </div>
    </PageLayout>
  );

  // Per-article auth gate (#2836). The help/:category/:slug route is public at
  // the router level; access is enforced here, fail-closed — only `isPublic`
  // articles render for guests. Everything else is wrapped in ProtectedRoute,
  // preserving the exact pre-#2836 redirect behavior (login modal + loader).
  // The hardcoded /help/sign-up route passes `propSlug`; its publicness is
  // declared at the route (registered outside ProtectedRoute), so it bypasses
  // the article flag and keeps working as an explicit public alias.
  if (!propSlug && !article.isPublic) {
    return <ProtectedRoute>{content}</ProtectedRoute>;
  }

  return content;
}
